'use client'

import { formatDate } from '@/date'
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline'

export const Comment = ({
  comment,
  onReply,
  depth = 0,
  children,
  isEven = false,
}: CommentProps) => {
  const handleReply = (e: React.MouseEvent) => {
    e.preventDefault()
    onReply(comment.id, comment.customerName)
  }

  return (
    <div className={depth > 0 ? 'ml-2 md:ml-6 pl-2 md:pl-4' : ''}>
      <div
        className={`${isEven ? 'bg-gray-50' : 'bg-white'} py-2 md:py-3 px-3 md:px-4`}
      >
        <div className="flex gap-2 md:gap-4">
          <div className="flex-shrink-0">
            {comment.gravatarUrl ? (
              <img
                src={comment.gravatarUrl}
                alt={comment.customerName}
                width={40}
                height={40}
                className="w-8 h-8 md:w-12 md:h-12 rounded-full border-2 border-gray-200"
              />
            ) : (
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center border-2 border-gray-200">
                <span className="text-gray-600 text-sm md:text-lg font-semibold">
                  {comment.customerName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div>
                <h4 className="font-semibold text-base md:text-lg text-gray-900 mb-1">
                  {comment.customerName}
                </h4>
                <time className="text-xs md:text-sm text-gray-500">
                  {formatDate(comment.time)}
                </time>
              </div>
              <button
                onClick={handleReply}
                className="inline-flex items-center gap-1 text-xs md:text-sm text-vi-red hover:text-red-700 hover:underline font-medium transition-colors self-start sm:self-auto cursor-pointer"
              >
                <ArrowUturnLeftIcon className="w-3 h-3 md:w-4 md:h-4" />
                Reply
              </button>
            </div>
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-wrap break-words text-gray-800 leading-relaxed">
                {comment.comment}
              </p>
            </div>
          </div>
        </div>
        {children && <div className="mt-3">{children}</div>}
      </div>
    </div>
  )
}

interface CommentProps {
  comment: {
    id: number
    customerId: number
    comment: string
    time: string
    responseTo?: number | null
    customerName: string
    gravatarUrl?: string | null
  }
  onReply: (commentId: number, customerName: string) => void
  depth?: number
  children?: React.ReactNode
  isEven?: boolean
}
